import { z } from 'zod';
import { User, Users } from 'lucide-react';
import {
  type TasksByAssigneeItem,
  OPEN_TASK_AGE_BUCKETS,
  ASSIGNEE_AGING_BUCKET_COUNT,
} from '@vagusx/clients';
import { TableTile } from '../table-tile';
import { drilldowns } from '../../drilldowns';
import { useDashboardMetric } from '@/hooks/use-dashboard-metric';
import { cn } from '@/lib/utils';
import type { TileDefinition } from './types';

export const tasksByAssigneeTile: TileDefinition = {
  type: 'tasks_by_assignee',
  title: 'Tasks by assignee',
  description: "Closure rate and where each person's open work sits by age.",
  icon: Users,
  metrics: ['tasks_by_assignee'],
  // Half-width default — pairs with another half-width tile (typically
  // throughput trend on the same row) so a manager sees flow + bottleneck
  // side by side. Resize to full width via Edit if more breathing room
  // is wanted for the 6 columns.
  defaultSize: { w: 6, h: 4 },
  minSize: { w: 4, h: 3 },
  configSchema: z.object({}).strict(),
  defaultConfig: {},
  render: ({ period, dashboardCtx, enabled }) => {
    const { metric, isLoading, isError } = useDashboardMetric({
      metricKey: 'tasks_by_assignee',
      from: period.from,
      to: period.to,
      enabled,
    });
    const items = metric?.items ?? [];
    // Org-wide open total — used to compute each row's "% of total
    // open queue" which sits next to the per-row open count.
    const orgOpen = items.reduce((sum, r) => sum + r.openCount, 0);
    return (
      <TableTile
        columns={[
          {
            key: 'assignee',
            header: 'Assignee',
            render: (r) => {
              const Icon = r.assigneeType === 'team' ? Users : User;
              return (
                <span className="inline-flex items-center gap-1.5">
                  <Icon
                    className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                    aria-label={r.assigneeType === 'team' ? 'Team' : 'User'}
                  />
                  <span className="truncate">{r.assigneeName}</span>
                </span>
              );
            },
            getDrilldown: (r) => drilldowns.tasksByAssigneeRow(r, dashboardCtx),
          },
          {
            key: 'closurePct',
            header: 'Closed %',
            align: 'right',
            render: (r) => {
              const total = r.openCount + r.completedCount;
              if (total === 0) return '—';
              const p = (r.completedCount / total) * 100;
              return (
                <span className={cn('tabular-nums', p < 80 && 'font-medium text-destructive')}>
                  {p.toFixed(0)}%
                </span>
              );
            },
          },
          {
            key: 'open',
            header: 'Open',
            align: 'right',
            render: (r) => {
              if (r.openCount === 0) return '—';
              const sharePct = orgOpen === 0 ? 0 : (r.openCount / orgOpen) * 100;
              return (
                <span className="tabular-nums">
                  {r.openCount.toLocaleString()}{' '}
                  <span className="text-[10px] text-muted-foreground/70">
                    {sharePct.toFixed(0)}%
                  </span>
                </span>
              );
            },
            getDrilldown: (r) => drilldowns.tasksByAssigneeOpen(r, dashboardCtx),
          },
          // Three aging buckets — surfaces 1-3 / 3-7 / 7-14 day buckets;
          // the <1d bucket is folded into "Open" total. Pulls from the
          // canonical OPEN_TASK_AGE_BUCKETS, sliced to the assignee
          // tile's display range.
          ...OPEN_TASK_AGE_BUCKETS.slice(1, ASSIGNEE_AGING_BUCKET_COUNT).map(
            (bucket, displayIdx) => ({
              key: `bucket_${displayIdx + 1}`,
              header: bucket.label,
              align: 'right' as const,
              render: (r: TasksByAssigneeItem) => {
                const bucketIdx = displayIdx + 1;
                const count = r.bucketCounts[bucketIdx] ?? 0;
                if (count === 0) return '—';
                const isWorst =
                  bucketIdx === ASSIGNEE_AGING_BUCKET_COUNT - 1 &&
                  r.openCount > 0 &&
                  count / r.openCount >= 0.3;
                return (
                  <span className={cn('tabular-nums', isWorst && 'font-medium text-destructive')}>
                    {count}
                  </span>
                );
              },
              getDrilldown: (r: TasksByAssigneeItem) =>
                drilldowns.agingHeatmapCell(r, bucket, dashboardCtx),
            })
          ),
        ]}
        rows={items}
        rowKey={(r) => `${r.assigneeType}-${r.assigneeId}`}
        isLoading={isLoading}
        isError={isError}
        emptyMessage="No assigned tasks"
      />
    );
  },
};
